import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import SvgColor from 'src/components/svg-color';
import { fTimeAgo } from 'src/utils/format-time';
import LoadingDots from 'src/components/loading-dots';
import FlagMenu from 'src/components/flag-menu/flag-menu';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { addTag } from 'src/services/role.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useSnackbar } from 'notistack';
import Scrollbar from 'src/components/scrollbar';
import { editCandidateDetails } from 'src/services/candidate.service';

export default function JobCandidateRow({
  candidate,
  screeningStatusMap,
  onViewProfile,
  candidatePopover,
  setSelectedCandidatePopover,
  checkedCandidates,
  setCheckedCandidates,
  currentTab,
  checkAllCandidates,
  setCheckAllCandidates,
  setShowNavbar,
  setShowSelectionToolbar,
  showFilterResults,
  updateCandidateFlag,
  allTags,
  isFetchingTags,
  fetchTags,
}) {
  const {
    id,
    name,
    email,
    currentRole,
    currentOrg,
    rank,
    preScreeningQualified,
    screeningQualified,
    score,
    source,
    tags: savedTags,
    screeningStatus,
    isEmailSent,
    createdAt,
    current_stage_flag,
  } = candidate;

  // hooks
  const tagsPopover = usePopover();
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const { enqueueSnackbar } = useSnackbar();

  const [isChecked, setIsChecked] = useState(false);
  const initialFlag = current_stage_flag?.candidate_stage_flag_name || null;
  const [currentFlag, setCurrentFlag] = useState(initialFlag);
  const [showFlag, setShowFlag] = useState(initialFlag !== null);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [addNewTag, setAddNewTag] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [tags, setTags] = useState(savedTags || []);
  const [sortedTags, setSortedTags] = useState([]);
  const [tagExists, setTagExists] = useState(true);

  const updateCandFlag = flagName => {
    setCurrentFlag(flagName);
    const flag = {
      candidate_stage_flag_name: flagName,
    };
    updateCandidateFlag(candidate, flag);
  };

  const handleOnChange = e => {
    if (e.target.checked) {
      const _checkedCandidates = [
        ...checkedCandidates,
        { id, screeningStatus },
      ];
      setCheckedCandidates(_checkedCandidates);

      if (_checkedCandidates.length) {
        setShowNavbar(false);
        setShowSelectionToolbar(true);
      }
    } else {
      setCheckAllCandidates(false);
      const _checkedCandidates = checkedCandidates.filter(
        item => item.id !== id,
      );

      if (!_checkedCandidates.length) {
        setShowNavbar(true);
        setShowSelectionToolbar(false);
      }
      setCheckedCandidates(_checkedCandidates);
    }
  };

  const handleTagsPopover = () => {
    tagsPopover.onClose();
    setAddNewTag(false);
    setNewTagInput('');

    const _sortedTags = [...allTags];

    // Move selected tags to the beginning
    const selectedTags = _sortedTags.filter(tag =>
      tags.some(t => t.toLowerCase() === tag.toLowerCase()),
    );
    const unselectedTags = _sortedTags.filter(
      tag => !tags.some(t => t.toLowerCase() === tag.toLowerCase()),
    );

    // Sort selected tags alphabetically
    selectedTags.sort((a, b) => a.localeCompare(b));

    // Sort unselected tags alphabetically
    unselectedTags.sort((a, b) => a.localeCompare(b));

    // Combine the sorted arrays
    _sortedTags.length = 0;
    _sortedTags.push(...selectedTags);
    _sortedTags.push(...unselectedTags);

    // Update the state with the sorted tags
    setSortedTags(_sortedTags);
  };

  const handleAddNewTag = async () => {
    try {
      if (!newTagInput) {
        enqueueSnackbar('Tag name cannot be empty!', {
          variant: 'error',
        });
        return;
      }

      const payload = {
        candidate_tag: newTagInput,
      };

      const response = await addTag(orgId, payload);

      if (response.success) {
        await updateTags(newTagInput);

        const _allTags = await fetchTags();

        const _tags = [...tags, newTagInput];

        const _sortedTags = [..._allTags];

        // Move selected tags to the beginning
        const selectedTags = _sortedTags.filter(tag =>
          _tags.some(t => t.toLowerCase() === tag.toLowerCase()),
        );
        const unselectedTags = _sortedTags.filter(
          tag => !_tags.some(t => t.toLowerCase() === tag.toLowerCase()),
        );

        // Sort selected tags alphabetically
        selectedTags.sort((a, b) => a.localeCompare(b));

        // Sort unselected tags alphabetically
        unselectedTags.sort((a, b) => a.localeCompare(b));

        // Combine the sorted arrays
        _sortedTags.length = 0;
        _sortedTags.push(...selectedTags);
        _sortedTags.push(...unselectedTags);

        // Update the state with the sorted tags
        setSortedTags(_sortedTags);

        setAddNewTag(false);
        setNewTagInput('');
        enqueueSnackbar('Added new tag successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to add tag', {
        variant: 'error',
      });
    }
  };

  const updateTags = async tag => {
    try {
      const updatedTags = [...tags];
      const tagIndex = updatedTags.findIndex(
        t => t.toLowerCase() === tag.toLowerCase(),
      );

      if (tagIndex > -1) {
        updatedTags.splice(tagIndex, 1);
      } else {
        updatedTags.push(tag.charAt(0).toUpperCase() + tag.slice(1));
      }
      const payload = {
        tags: updatedTags,
      };

      const response = await editCandidateDetails(orgId, id, payload);

      if (response.success) {
        setTags(updatedTags);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to add tag', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (checkedCandidates.find(item => item.id === id)) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [checkedCandidates, id]);

  useEffect(() => {
    if (!isChecked) {
      setShowCheckbox(false);
    } else {
      setShowCheckbox(true);
    }
  }, [isChecked]);

  useEffect(() => {
    const newFlag =
      candidate.current_stage_flag?.candidate_stage_flag_name || null;
    setCurrentFlag(newFlag);
    setShowFlag(newFlag !== null);
  }, [candidate]);

  useEffect(() => {
    const _tagExists = tags.some(
      tag => tag.toLowerCase() === newTagInput.toLowerCase(),
    );
    setTagExists(_tagExists);
  }, [newTagInput, tags]);

  useEffect(() => {
    if (sortedTags.length === 0) {
      const _sortedTags = [...allTags];

      // Move selected tags to the beginning
      const selectedTags = _sortedTags.filter(tag =>
        tags.some(t => t.toLowerCase() === tag.toLowerCase()),
      );
      const unselectedTags = _sortedTags.filter(
        tag => !tags.some(t => t.toLowerCase() === tag.toLowerCase()),
      );

      // Sort selected tags alphabetically
      selectedTags.sort((a, b) => a.localeCompare(b));

      // Sort unselected tags alphabetically
      unselectedTags.sort((a, b) => a.localeCompare(b));

      // Combine the sorted arrays
      _sortedTags.length = 0;
      _sortedTags.push(...selectedTags);
      _sortedTags.push(...unselectedTags);

      // Update the state with the sorted tags
      setSortedTags(_sortedTags);
    }
  }, [allTags, sortedTags.length, tags]);


  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
        onMouseEnter={() => {
          setShowCheckbox(true);
          if (!currentFlag) setShowFlag(true);
        }}
        onMouseLeave={() => {
          if (!currentFlag) setShowFlag(false);
          if (!isChecked) setShowCheckbox(false);
        }}
      >
        {(checkedCandidates.length || showCheckbox) &&
          !showFilterResults &&
          currentTab !== 'REJECTED' && (
            <FormControlLabel
              sx={{
                position: 'absolute',
                left: '24px',
                top: '44%',
              }}
              control={
                <Checkbox
                  sx={{
                    '& .MuiSvgIcon-root': {
                      width: '20px',
                      height: '20px',
                    },
                  }}
                  checked={isChecked}
                  onChange={handleOnChange}
                />
              }
            />
          )}
        <Box
          sx={{
            border: theme =>
              `1px solid ${
                theme.palette.border[
                  isChecked ? 'primaryAccent1' : 'primaryBlack3'
                ]
              }`,
            borderRadius: '6px',
            backgroundColor: theme => theme.palette.background.default,
            px: '16px',
            py: '12px',
            mx: '50px',
            cursor:
              screeningStatus &&
              screeningStatusMap[screeningStatus]?.value ===
                screeningStatusMap.ANALYZING.value
                ? ''
                : 'pointer',
          }}
          onClick={onViewProfile}
        >
          <Stack spacing="22px">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing="20px">
                <Stack direction="row" alignItems="baseline" spacing="12px">
                  <Stack spacing="6px" alignItems="center">
                    <Typography
                      variant="subheading2"
                      color={theme => theme.palette.content.primaryBlack3}
                    >
                      {rank !== null ? rank : `//`}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={theme => theme.palette.content.primaryBlack3}
                    >
                      Rank
                    </Typography>
                  </Stack>
                  <Stack spacing="6px">
                    <Typography variant="subheading2" fontWeight={500}>
                      {name || email}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={theme => theme.palette.content.primaryBlack3}
                    >
                      {`${currentRole}${currentOrg ? ` @${currentOrg}` : ''}`}
                    </Typography>
                  </Stack>
                </Stack>
                {showFlag ? (
                  <Stack>
                    <FlagMenu
                      updateCandFlag={updateCandFlag}
                      currentFlag={currentFlag}
                      candidate_id={id}
                    />
                  </Stack>
                ) : null}
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                spacing="2px"
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                {createdAt && (
                  <Typography
                    variant="caption"
                    color={theme => theme.palette.content.primaryBlack3}
                  >
                    {fTimeAgo(createdAt)}
                  </Typography>
                )}

                {isEmailSent !== null &&
                  screeningStatus &&
                  screeningStatus !== 'CANDIDATE_HIRED' && (
                    <>
                      {!isEmailSent && (
                        <IconButton>
                          <Tooltip title="Send email">
                            <Iconify icon="tabler:mail" />
                          </Tooltip>
                        </IconButton>
                      )}
                      {isEmailSent && (
                        <Box
                          sx={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title="Email sent">
                            <Iconify icon="tabler:mail-check" />
                          </Tooltip>
                        </Box>
                      )}
                    </>
                  )}
                {screeningStatus && (
                  <IconButton
                    disabled={
                      screeningStatusMap[screeningStatus]?.value ===
                      screeningStatusMap.ANALYZING.value
                    }
                    onClick={e => {
                      setSelectedCandidatePopover(candidate);
                      candidatePopover.onOpen(e);
                      e.stopPropagation();
                    }}
                  >
                    <Iconify icon="tabler:dots-vertical" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing="9px"
            >
              <Stack
                onClick={e => {
                  e.stopPropagation();
                }}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                {screeningStatus &&
                  screeningStatusMap[screeningStatus]?.value ===
                    screeningStatusMap.ANALYZING.value && (
                    <Tooltip title="Analyzing resume, please check after sometime">
                      <Label
                        color="info"
                        startIcon={
                          <SvgColor
                            sx={{
                              width: 18,
                              height: 18,
                            }}
                            src="/assets/icons/job/ic_star_twinkle.svg"
                          />
                        }
                      >
                        <Stack direction="row" alignItems="baseline">
                          {screeningStatusMap[screeningStatus]?.label}
                          <LoadingDots color="#5418EF" />
                        </Stack>
                      </Label>
                    </Tooltip>
                  )}
                {preScreeningQualified !== null && (
                  <Tooltip
                    title={preScreeningQualified ? 'Qualified' : 'Disqualified'}
                  >
                    <Label
                      color={preScreeningQualified ? 'success' : 'error'}
                      startIcon={
                        <SvgColor
                          sx={{
                            width: 18,
                            height: 18,
                          }}
                          src="/assets/icons/job/ic_star_twinkle.svg"
                        />
                      }
                    >
                      Pre-screening
                    </Label>
                  </Tooltip>
                )}
                {screeningQualified !== null && (
                  <Tooltip
                    title={screeningQualified ? 'Qualified' : 'Disqualified'}
                  >
                    <Label
                      color={screeningQualified ? 'success' : 'error'}
                      startIcon={
                        <SvgColor
                          sx={{
                            width: 18,
                            height: 18,
                          }}
                          src="/assets/icons/job/ic_star_twinkle.svg"
                        />
                      }
                    >
                      Screening
                    </Label>
                  </Tooltip>
                )}
                {score !== null && (
                  <Tooltip title="AI Interview">
                    <Label
                      color="info"
                      startIcon={
                        <SvgColor
                          sx={{
                            width: 18,
                            height: 18,
                          }}
                          src="/assets/icons/job/ic_star_twinkle.svg"
                        />
                      }
                    >
                      AI Interview score - {`${score}`}
                    </Label>
                  </Tooltip>
                )}
              </Stack>
              <Stack
                onClick={e => {
                  e.stopPropagation();
                }}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                {tags && tags.length > 0 ? (
                  <Tooltip
                    title={tags
                      .map(tag => tag.charAt(0).toUpperCase() + tag.slice(1))
                      .join(', ')}
                  >
                    <Label
                      sx={{
                        background: theme =>
                          theme.palette.background.primaryGrey1,
                        cursor: 'pointer',
                      }}
                      onClick={event => {
                        tagsPopover.onOpen(event);
                      }}
                    >
                      {tags[0]}
                      {`${tags?.length > 1 ? ` +${tags.length - 1}` : ''}`}
                    </Label>
                  </Tooltip>
                ) : (
                  <Tooltip title="Add Tag">
                    <Label
                      sx={{
                        background: theme =>
                          theme.palette.background.primaryGrey1,
                        cursor: 'pointer',
                      }}
                      onClick={event => {
                        tagsPopover.onOpen(event);
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing="8px">
                        <Iconify icon="tabler:plus" />
                        <Typography variant="caption">Add tag</Typography>
                      </Stack>
                    </Label>
                  </Tooltip>
                )}
                {source && (
                  <Tooltip title="Source">
                    <Label>{source}</Label>
                  </Tooltip>
                )}
                {screeningStatus &&
                  screeningStatusMap[screeningStatus]?.value !==
                    screeningStatusMap.ANALYZING.value && (
                    <Tooltip title="Status">
                      <Label
                        sx={{ color: theme => theme.palette.common.black }}
                        color="warning"
                      >
                        {screeningStatusMap[screeningStatus]?.label}
                      </Label>
                    </Tooltip>
                  )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <CustomPopover
        open={tagsPopover.open}
        onClose={handleTagsPopover}
        sx={{ width: 200 }}
      >
        <Scrollbar
          sx={{
            height: '180px',
            overflowY: 'auto',
          }}
        >
          {sortedTags &&
            sortedTags
              .filter(tag =>
                tag.toLowerCase().includes(newTagInput.toLowerCase()),
              )
              .map(tag => {
                const isTagFound = tags
                  ? tags.map(t => t.toLowerCase()).includes(tag.toLowerCase())
                  : false;

                return (
                  <MenuItem
                    key={tag}
                    selected={isTagFound}
                    onClick={() => {
                      updateTags(tag);
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography variant="caption">{tag}</Typography>
                      {isTagFound && <Iconify icon="tabler:check" width={20} />}
                    </Stack>
                  </MenuItem>
                );
              })}
        </Scrollbar>
        {addNewTag ? (
          <TextField
            value={newTagInput}
            onChange={e => setNewTagInput(e.target.value)}
            placeholder="Enter tag"
            sx={{
              my: '8px',
              '& .MuiOutlinedInput-root': {
                width: 180,
              },
              '& .MuiOutlinedInput-input': {
                minWidth: 0,
              },
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                if (tagExists || !newTagInput) {
                  enqueueSnackbar('Tag already exists!', {
                    variant: 'error',
                  });
                  return;
                }
                handleAddNewTag();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    width="28px"
                    height="28px"
                    borderRadius="4px"
                    bgcolor={theme =>
                      theme.palette.background[
                        !tagExists || newTagInput
                          ? 'primaryAccent2'
                          : 'primaryGrey3'
                      ]
                    }
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      if (tagExists || !newTagInput) {
                        enqueueSnackbar('Tag already exists!', {
                          variant: 'error',
                        });
                        return;
                      }
                      handleAddNewTag();
                    }}
                  >
                    <Iconify
                      sx={{
                        color: theme =>
                          theme.palette.content[
                            !tagExists && newTagInput
                              ? 'accent1'
                              : 'primaryBlack2'
                          ],
                      }}
                      icon="tabler:chevron-right"
                    />
                  </Stack>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <MenuItem
            onClick={() => {
              setAddNewTag(true);
            }}
          >
            <Iconify icon="tabler:plus" />
            Add New
          </MenuItem>
        )}
      </CustomPopover>
    </>
  );
}

JobCandidateRow.propTypes = {
  candidate: PropTypes.object.isRequired,
  screeningStatusMap: PropTypes.object.isRequired,
  onViewProfile: PropTypes.func.isRequired,
  candidatePopover: PropTypes.object.isRequired,
  setSelectedCandidatePopover: PropTypes.func.isRequired,
  checkedCandidates: PropTypes.array.isRequired,
  setCheckedCandidates: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
  checkAllCandidates: PropTypes.bool.isRequired,
  setCheckAllCandidates: PropTypes.func.isRequired,
  setShowNavbar: PropTypes.func.isRequired,
  setShowSelectionToolbar: PropTypes.func.isRequired,
  showFilterResults: PropTypes.bool.isRequired,
  updateCandidateFlag: PropTypes.func.isRequired,
  isFetchingTags: PropTypes.bool,
  allTags: PropTypes.array,
  fetchTags: PropTypes.func,
};
